import React from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserConfigProvider } from "@/context/UserConfigContext";   // ⬅️ NEW
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { router } from "./router";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/context/AuthContext";
import { MotionConfig } from "framer-motion";
import * as Sentry from "@sentry/react";

class ToasterErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.warn('[ToasterErrorBoundary] Caught toast render error:', error.message);
  }

  render() {
    if (this.state.hasError) {
      // Reset on next render cycle so toasts can recover
      setTimeout(() => this.setState({ hasError: false }), 0);
      return null;
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 2
    }
  }
});

const App = () => {
  return (
  <MotionConfig reducedMotion="user">
  <ThemeProvider defaultTheme="system" storageKey="avpro-bms-theme">
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
          {/* keep the provider alive across every route */}
          <UserConfigProvider>
            <TooltipProvider delayDuration={300}>
              <ToasterErrorBoundary>
                <Sonner />   {/* bottom-right is already the default in our wrapper */}
              </ToasterErrorBoundary>
              <RouterProvider
                router={router}
                future={{
                  v7_startTransition: true
                }}
              />
            </TooltipProvider>
          </UserConfigProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ThemeProvider>
  </MotionConfig>
  );
};

const AppWithProfiler = Sentry.withProfiler(App, { name: "App" });
export default AppWithProfiler;
